import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { patientPortal } from '@/api/patientPortal';
import {
  DELETE_ACCOUNT_PATH,
  SUPPORT_EMAIL,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_TEL,
} from '@/constants/patientPortalContact';

export default function PatientPortalDeleteAccount() {
  const navigate = useNavigate();
  const [loggedIn, setLoggedIn] = useState(false);
  const [reason, setReason] = useState('');
  const [phone, setPhone] = useState('');
  const [uhid, setUhid] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [msg, setMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteUrl, setDeleteUrl] = useState(
    typeof window !== 'undefined' ? `${window.location.origin}${DELETE_ACCOUNT_PATH}` : ''
  );

  useEffect(() => {
    const token = localStorage.getItem('patient_token');
    setLoggedIn(!!token);
    patientPortal
      .getAccountDeletionInfo()
      .then((info: { deleteAccountUrl?: string }) => {
        if (info?.deleteAccountUrl) setDeleteUrl(info.deleteAccountUrl);
      })
      .catch(() => {});
  }, []);

  const submitAuthenticated = async () => {
    if (!confirm) {
      setMsg('Please confirm that you understand what will be deleted.');
      return;
    }
    setMsg('');
    setSubmitting(true);
    try {
      await patientPortal.requestAccountDeletion(reason.trim() || undefined);
      localStorage.removeItem('patient_token');
      setMsg(
        'Your TrueCare app login has been removed. You have been signed out. Hospital medical records are retained as required by law.'
      );
      setLoggedIn(false);
      setTimeout(() => navigate('/patient-loyalty/login'), 4000);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setMsg(err.response?.data?.message ?? 'Could not process deletion.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitPublic = async () => {
    setMsg('');
    setSubmitting(true);
    try {
      const result = await patientPortal.requestAccountDeletionPublic({
        phone: phone.trim(),
        uhid: uhid.trim() || undefined,
        reason: reason.trim(),
      });
      setMsg((result as { message?: string })?.message ?? 'Request sent. Our team will contact you if needed.');
      setReason('');
      setPhone('');
      setUhid('');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setMsg(err.response?.data?.message ?? 'Could not submit request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="max-w-lg mx-auto px-4 py-10">
        <div className="mb-6">
          <Link to="/patient-loyalty/login" className="text-sm text-teal-800 font-medium hover:underline">
            ← TrueCare patient portal
          </Link>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/80 space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h1 className="text-xl font-bold text-slate-900">Delete TrueCare app account</h1>
              <p className="text-sm text-slate-600 mt-1">
                Use this page to request removal of your <strong>mobile and web app login</strong> (TrueCare
                patient loyalty app).
              </p>
            </div>
          </div>

          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700 space-y-2">
            <p>
              <strong>What is deleted:</strong> App password, OTP sessions, push notification tokens, and
              ability to sign in to the TrueCare app.
            </p>
            <p>
              <strong>What is kept:</strong> Your hospital registration, UHID, bills, prescriptions, and clinical
              records at True Hospitals (required for healthcare and legal compliance).
            </p>
            <p className="text-xs text-slate-500 break-all">
              Public URL for app stores: {deleteUrl}
            </p>
          </div>

          {msg ? (
            <p
              className={`text-sm rounded-lg p-3 ${
                msg.includes('removed') || msg.includes('sent')
                  ? 'bg-teal-50 text-teal-900 border border-teal-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {msg}
            </p>
          ) : null}

          {loggedIn ? (
            <div className="space-y-3 border-t border-slate-100 pt-4">
              <p className="text-sm font-medium text-slate-800">You are signed in</p>
              <p className="text-sm text-slate-600">
                Submitting below will <strong>immediately</strong> remove your app access and sign you out.
              </p>
              <textarea
                className="w-full min-h-[80px] border border-slate-200 rounded-lg px-3 py-2 text-sm"
                placeholder="Optional: reason for deletion"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirm}
                  onChange={(e) => setConfirm(e.target.checked)}
                  className="mt-1"
                />
                I understand that my app login will be removed and hospital medical records may be retained.
              </label>
              <button
                type="button"
                onClick={submitAuthenticated}
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-red-700 text-white py-3 text-sm font-semibold hover:bg-red-800 disabled:opacity-60"
              >
                <Trash2 className="h-4 w-4" />
                {submitting ? 'Processing…' : 'Delete my app account'}
              </button>
            </div>
          ) : (
            <div className="space-y-3 border-t border-slate-100 pt-4">
              <p className="text-sm text-slate-600">
                <Link to="/patient-loyalty/login" className="text-teal-800 font-medium hover:underline">
                  Sign in
                </Link>{' '}
                to delete your account immediately, or submit a request without signing in:
              </p>
              <label className="block text-sm">
                Registered mobile number *
                <input
                  type="tel"
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </label>
              <label className="block text-sm">
                UHID (if you have multiple profiles)
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={uhid}
                  onChange={(e) => setUhid(e.target.value)}
                />
              </label>
              <label className="block text-sm">
                Reason for deletion *
                <textarea
                  className="mt-1 w-full min-h-[80px] border rounded-lg px-3 py-2"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                />
              </label>
              <button
                type="button"
                onClick={submitPublic}
                disabled={submitting}
                className="w-full rounded-xl bg-red-700 text-white py-3 text-sm font-semibold hover:bg-red-800 disabled:opacity-60"
              >
                {submitting ? 'Sending…' : 'Submit deletion request'}
              </button>
            </div>
          )}

          <p className="text-xs text-slate-500 border-t border-slate-100 pt-4">
            Questions? Call{' '}
            <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-teal-800 hover:underline">
              {SUPPORT_PHONE_DISPLAY}
            </a>{' '}
            or email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-teal-800 hover:underline">
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
